import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../services/api";
import { Button } from "../components/ui/Button";

const defaultForm = {
  tenant_id: null,
  company_name: "Minha Empresa",
  cnpj: "",
  support_email: "",
  timezone: "America/Sao_Paulo",

  ms_tenant_id: "",
  ms_client_id: "",
  ms_client_secret: "",
  ms_monitored_email: "",

  email_sync_enabled: false,
  email_sync_interval_seconds: 180,

  default_ticket_status: "Aberto",
  default_ticket_priority: "Media",
  allow_attachments: true,
  max_attachment_size_mb: 10,
  hide_cancelled_from_main_view: true,
};

const tabList = [
  { id: "general", label: "Geral" },
  { id: "microsoft", label: "E-mail e Microsoft 365" },
  { id: "tickets", label: "Chamados" },
];

const Settings = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tenantId = searchParams.get("tenant_id");
  const activeTenantId = localStorage.getItem("active_tenant_id") || "";
  const activeTenantName = localStorage.getItem("active_tenant_name") || "";
  const effectiveTenantId = activeTenantId || tenantId || "";

  const [activeTab, setActiveTab] = useState("general");
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loggedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  }, []);

  const isAdmin =
    loggedUser?.profile?.toLowerCase() === "administrador" ||
    loggedUser?.profile?.toLowerCase() === "admin";
  const isPlatformAdmin = !!loggedUser?.is_platform_admin;

  useEffect(() => {
    fetchSettings();
  }, [effectiveTenantId]);

  useEffect(() => {
    if (activeTenantId && tenantId && String(activeTenantId) !== String(tenantId)) {
      navigate("/settings", { replace: true });
    }
  }, [activeTenantId, tenantId, navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError("");

      const params = {};
      if (effectiveTenantId) {
        params.tenant_id = effectiveTenantId;
      }

      const response = await api.get("/settings", { params });

      setForm({
        ...defaultForm,
        ...response.data,
      });
    } catch (err) {
      console.error("Erro ao carregar configurações:", err);
      setError(err.response?.data?.detail || "Erro ao carregar configurações.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const payload = {
        tenant_id: effectiveTenantId ? Number(effectiveTenantId) : form.tenant_id,
        company_name: form.company_name,
        cnpj: form.cnpj || null,
        support_email: form.support_email || null,
        timezone: form.timezone,

        ms_tenant_id: form.ms_tenant_id || null,
        ms_client_id: form.ms_client_id || null,
        ms_client_secret: form.ms_client_secret || null,
        ms_monitored_email: form.ms_monitored_email || null,

        email_sync_enabled: !!form.email_sync_enabled,
        email_sync_interval_seconds: Number(form.email_sync_interval_seconds),

        default_ticket_status: form.default_ticket_status,
        default_ticket_priority: form.default_ticket_priority,
        allow_attachments: !!form.allow_attachments,
        max_attachment_size_mb: Number(form.max_attachment_size_mb),
        hide_cancelled_from_main_view: !!form.hide_cancelled_from_main_view,
      };

      const params = {};
      if (effectiveTenantId) {
        params.tenant_id = effectiveTenantId;
      }

      const response = await api.put("/settings", payload, { params });

      setForm({
        ...defaultForm,
        ...response.data,
      });

      setMessage("Configurações salvas com sucesso.");
    } catch (err) {
      console.error("Erro ao salvar configurações:", err);
      setError(err.response?.data?.detail || "Erro ao salvar configurações.");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            Você não tem permissão para acessar esta tela.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-start gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
            <p className="text-gray-500">
              Configure dados gerais, integrações e regras da empresa.
            </p>
            {(activeTenantName || form.company_name) && (
              <p className="mt-2 text-sm text-blue-700 font-medium">
                Empresa atual: {activeTenantName || form.company_name}
              </p>
            )}
          </div>

          {isPlatformAdmin && (
            <div>
              <Button type="button" variant="outline" onClick={() => navigate("/tenants")}>
                Voltar para empresas
              </Button>
            </div>
          )}
        </div>

        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <div className="flex flex-wrap gap-2">
              {tabList.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-md px-4 py-2 text-sm font-medium ${
                    activeTab === tab.id
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {loading ? (
              <div className="text-gray-500">Carregando configurações...</div>
            ) : (
              <>
                {message && (
                  <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-green-700">
                    {message}
                  </div>
                )}

                {error && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                    {error}
                  </div>
                )}

                {activeTab === "general" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Nome da empresa
                      </label>
                      <input
                        type="text"
                        value={form.company_name}
                        onChange={(e) => handleChange("company_name", e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                        required
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        CNPJ
                      </label>
                      <input
                        type="text"
                        value={form.cnpj || ""}
                        onChange={(e) => handleChange("cnpj", e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                        placeholder="00.000.000/0000-00"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        E-mail de suporte
                      </label>
                      <input
                        type="email"
                        value={form.support_email || ""}
                        onChange={(e) => handleChange("support_email", e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Fuso horário
                      </label>
                      <input
                        type="text"
                        value={form.timezone}
                        onChange={(e) => handleChange("timezone", e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                        required
                      />
                    </div>
                  </div>
                )}

                {activeTab === "microsoft" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Tenant ID
                      </label>
                      <input
                        type="text"
                        value={form.ms_tenant_id || ""}
                        onChange={(e) => handleChange("ms_tenant_id", e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Client ID
                      </label>
                      <input
                        type="text"
                        value={form.ms_client_id || ""}
                        onChange={(e) => handleChange("ms_client_id", e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Client Secret
                        {form.ms_client_secret_set && (
                          <span className="ml-2 text-xs font-normal text-green-600">
                            🔒 configurado
                          </span>
                        )}
                      </label>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={form.ms_client_secret || ""}
                        onChange={(e) => handleChange("ms_client_secret", e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                        placeholder={
                          form.ms_client_secret_set
                            ? "•••••••• (deixe em branco para manter o atual)"
                            : "cole o client secret aqui"
                        }
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Por segurança o segredo não é exibido. Preencha apenas para trocá-lo.
                      </p>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        E-mail monitorado
                      </label>
                      <input
                        type="email"
                        value={form.ms_monitored_email || ""}
                        onChange={(e) => handleChange("ms_monitored_email", e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Intervalo de sincronização em segundos
                      </label>
                      <input
                        type="number"
                        min="30"
                        value={form.email_sync_interval_seconds}
                        onChange={(e) =>
                          handleChange("email_sync_interval_seconds", e.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!form.email_sync_enabled}
                          onChange={(e) =>
                            handleChange("email_sync_enabled", e.target.checked)
                          }
                        />
                        Ativar leitura automática de e-mails
                      </label>
                    </div>
                  </div>
                )}

                {activeTab === "tickets" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Status padrão do chamado
                      </label>
                      <select
                        value={form.default_ticket_status}
                        onChange={(e) =>
                          handleChange("default_ticket_status", e.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                      >
                        <option value="Aberto">Aberto</option>
                        <option value="Em Andamento">Em Andamento</option>
                        <option value="Encerrado">Encerrado</option>
                        <option value="Cancelado">Cancelado</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Prioridade padrão
                      </label>
                      <select
                        value={form.default_ticket_priority}
                        onChange={(e) =>
                          handleChange("default_ticket_priority", e.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                      >
                        <option value="Baixa">Baixa</option>
                        <option value="Media">Media</option>
                        <option value="Alta">Alta</option>
                        <option value="Urgente">Urgente</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Tamanho máximo de anexo em MB
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={form.max_attachment_size_mb}
                        onChange={(e) =>
                          handleChange("max_attachment_size_mb", e.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </div>

                    <div className="flex flex-col gap-3 justify-center">
                      <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!form.allow_attachments}
                          onChange={(e) =>
                            handleChange("allow_attachments", e.target.checked)
                          }
                        />
                        Permitir anexos
                      </label>

                      <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!form.hide_cancelled_from_main_view}
                          onChange={(e) =>
                            handleChange(
                              "hide_cancelled_from_main_view",
                              e.target.checked
                            )
                          }
                        />
                        Ocultar chamados cancelados da tela principal
                      </label>
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button type="submit" disabled={saving}>
                    {saving ? "Salvando..." : "Salvar configurações"}
                  </Button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
